import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { BillFile } from '../../models';

@Component({
  selector: 'app-bill-preview',
  standalone: true,
  imports: [CommonModule, NgxExtendedPdfViewerModule],
  template: `
    <div class="preview-backdrop" (click)="close.emit()"></div>
    <div class="preview-panel" [class.maximized]="maximized">
      <div class="preview-header">
        <div class="header-info">
          <i class="material-symbols-rounded">description</i>
          <span class="file-name">{{ bill.file.name }}</span>
        </div>
        <div class="header-actions">
          <button class="header-btn" (click)="zoomOut()" title="Zoom out">
            <i class="material-symbols-rounded">zoom_out</i>
          </button>
          <span class="zoom-label">{{ zoomLevel }}%</span>
          <button class="header-btn" (click)="zoomIn()" title="Zoom in">
            <i class="material-symbols-rounded">zoom_in</i>
          </button>
          <button class="header-btn" (click)="fitWidth()" title="Fit to width">
            <i class="material-symbols-rounded">fit_width</i>
          </button>
          <div class="action-divider"></div>
          <button class="header-btn" (click)="maximized = !maximized" [title]="maximized ? 'Restore' : 'Maximize'">
            <i class="material-symbols-rounded">{{ maximized ? 'close_fullscreen' : 'open_in_full' }}</i>
          </button>
          <button class="header-btn close" (click)="close.emit()" title="Close">
            <i class="material-symbols-rounded">close</i>
          </button>
        </div>
      </div>
      <div class="preview-body">
        @if (pdfSrc) {
          <ngx-extended-pdf-viewer
            [src]="pdfSrc"
            [zoom]="currentZoom"
            [showToolbar]="false"
            [showSidebarButton]="false"
            [showFindButton]="false"
            [showPagingButtons]="false"
            [showZoomButtons]="false"
            [showPresentationModeButton]="false"
            [showOpenFileButton]="false"
            [showPrintButton]="false"
            [showDownloadButton]="false"
            [showSecondaryToolbarButton]="false"
            [showRotateButton]="false"
            [showHandToolButton]="false"
            [showScrollingButton]="false"
            [showSpreadButton]="false"
            [showPropertiesButton]="false"
            [textLayer]="true"
            [backgroundColor]="'#f1f5f9'"
            (zoomChange)="onZoomChange($event)"
            height="100%"
          ></ngx-extended-pdf-viewer>
        } @else {
          <div class="pdf-error">
            <i class="material-symbols-rounded">error</i>
            <p>Unable to load PDF preview</p>
          </div>
        }
      </div>
    </div>
  `,
  styleUrl: './bill-preview.component.scss'
})
export class BillPreviewComponent implements OnInit {
    @Input() bill!: BillFile;
    @Output() close = new EventEmitter<void>();

    pdfSrc: string | Uint8Array | null = null;
    maximized = false;
    currentZoom: string | number = 'page-width';
    zoomLevel = 100;

    ngOnInit() {
        this.pdfSrc = this.getPdfSrc(this.bill);
    }

    zoomIn() {
        this.zoomLevel = Math.min(this.zoomLevel + 25, 400);
        this.currentZoom = this.zoomLevel;
    }

    zoomOut() {
        this.zoomLevel = Math.max(this.zoomLevel - 25, 25);
        this.currentZoom = this.zoomLevel;
    }

    fitWidth() {
        this.currentZoom = 'page-width';
    }

    onZoomChange(zoom: any) {
        if (typeof zoom === 'number') {
            this.zoomLevel = Math.round(zoom);
        } else if (typeof zoom === 'string' && zoom.endsWith('%')) {
            this.zoomLevel = Math.round(parseFloat(zoom));
        }
    }

    private getPdfSrc(bill: BillFile): string | Uint8Array | null {
        if (bill.fileData?.length) return bill.fileData;
        if (bill.previewUrl) return bill.previewUrl;
        if (bill.file?.size > 0) {
            bill.previewUrl = URL.createObjectURL(bill.file);
            return bill.previewUrl;
        }
        return null;
    }
}
